import { Moment } from 'moment';
import * as moment from 'moment';
import * as mt from 'moment-timezone';
import { GLOBAL_SETTINGS } from '@shared/constants/global-settings';

export const DEFAULT_DATE_FORMAT = 'DD-MM-yyyy';
export const DATE_WITH_TIME_FORMAT = 'DD-MM-yyyy HH:mm';
export const DATE_FORMAT_FOR_TASK_REPORTS = 'yyyy-MM-DD';

export function formatDateToString(date: Date | Moment | string, dateFormat: string = DEFAULT_DATE_FORMAT): string {
  const endOfDate = moment(date).endOf('day').utc();
  if (endOfDate.utc().format() <= moment(new Date()).utc().format()) {
    return `overdue (${moment(endOfDate, 'YYYYMMDD').fromNow(true)})`;
  }
  return moment(date).format(dateFormat);
}

export function formatDateSimple(date: Date | Moment | string, dateFormat: string = DEFAULT_DATE_FORMAT): string {
  return moment(date).format(dateFormat);
}

export function formatUTCDateSimple(date: Date | Moment | string, dateFormat: string = DEFAULT_DATE_FORMAT): string {
  return moment.utc(date).format(dateFormat);
}

export function formatDateToLocalSimple(date: Date | Moment | string, dateFormat: string = DEFAULT_DATE_FORMAT): string {
  return moment(new Date(date?.toLocaleString() + 'Z')).format(dateFormat);
}

export function getEndOfDate(date: Date): Date {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}

export function getSameStartOfDate(date: any): Date | null {
  const parseDate = new Date(date);
  parseDate.setUTCHours(0o0, 0o0, 0o0, 0o00);
  return parseDate;
}

export function getSameEndOfDate(date: Date): Date {
  const parseDate = new Date(date);
  parseDate.setUTCHours(23, 59, 59, 999);
  return parseDate;
}

export function getStartOfDate(date: Date): Date {
  const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  return newDate;
}

export function getLocalDateAsString(date: Date): string {
  return moment(getStartOfDate(new Date(date))).format();
}

export function getClientTimezoneOffset(): number {
  return -new Date().getTimezoneOffset();
}

export function getAge(birth, death?): number {
  const birthDate = new Date(birth);
  const fromDate = death ? new Date(death) : new Date();
  return moment(fromDate).diff(birthDate, 'years');
}

export function formatDateSimpleforReport(date: Date | Moment | string, dateFormat: string = DATE_FORMAT_FOR_TASK_REPORTS): string {
  return moment(date).format(dateFormat);
}
export function formateDateTimeSimple(date: Date | string, dateFormat: string = 'YYYY-MM-DD, HH:mm:ss'): string {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  return moment.utc(date).format(dateFormat);
}
export function profileDateTimeformate(date: Date | string, dateFormat: string = 'DD-MM-YYYY, HH:mm'): string {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  return moment.utc(date).format(dateFormat);
}

export function formateUTCtoTZ(date: Date | string, dateFormat: string = 'MM-DD-YYYY hh:mm A'): string {
  const timeZone = localStorage.getItem(GLOBAL_SETTINGS.timeZoneKey) || 'Canada/Eastern';
  // The API always returns the date in UTC.
  // But it's formate is not in UTC formate, convert it to UTC first,
  // then change it to the desired timezone.
  if (typeof date === 'string' && !date.includes('Z')) {
    const utcMoment = moment.utc(date);
    return utcMoment.tz(timeZone).format(dateFormat);
  }

  return mt(date).tz(timeZone).format(dateFormat);
}



// Convert any date from given timezone to desired timezone
export function formateTZtoTZ(date: Date | string, fromTZ = 'Canada/Eastern', dateFormat: string = 'DD-MM-YYYY hh:mm A'): string {
  const utcDate = mt.tz(date, fromTZ).utc().format();
  return formateUTCtoTZ(utcDate, dateFormat);
}

export function isLessThanToday(date: string, format: string = 'YYYY-MM-DD'): boolean {
  const givenDate = moment(date, format);
  const currentDate = moment().startOf('day');
  return givenDate.isBefore(currentDate, 'day');
}

export function transformDateToMMDDYYYY(date: string) {
  return moment(date, 'DD-MM-YYYY hh:mm A').format('MM-DD-YYYY hh:mm A');
}

// example output January 29th, 2025 12:00 AM
export function formatDateWithOrdinal(dateString: string): string {
  return moment(dateString).format('MMMM Do, YYYY h:mm A');
}

// example output January 23rd, 2025 - January 30th, 2025
export function formatDateRangeWithOrdinal(dateRange: string): string {
  const [start, end] = dateRange.split(' - ').map(date => moment(date.trim()));
  return `${start.format('MMMM Do, YYYY')} - ${end.format('MMMM Do, YYYY')}`;
}

export function parseCustomDate(dateString: string): Date {
  // Remove ordinal suffixes (th, st, nd, rd) from the date string
  const cleanedDateString = dateString.replace(/(\d+)(st|nd|rd|th)/, '$1');
  const formattedDateString = cleanedDateString.replace('12:00 AM', '00:00');
  return new Date(formattedDateString);
}

// Function to set timestamp as 00:00:00.000 for dates.
// Example output "2025-03-30T00:00:00.000"
export function getEndOfDateWithoutTimeStamp(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000`;
}
export function formatDateOnly(date: Date | string): string {
  if (!date) { return ''; }
  const parsedDate = typeof date === 'string' ? new Date(date) : date;
  return moment(parsedDate).format('MM/DD/YYYY');
}

export function formatDateWithDateSeparator(date: Date | Moment | string, dateFormat: string = 'MM-DD-YYYY'): string {
  if (!date) { return ''; }

  if (date instanceof Date || moment.isMoment(date)) {
    return moment(date).format(dateFormat);
  }


  const parsed = moment(date, [
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    'YYYY-MM-DD',
    'YYYY/MM/DD',
    'MM-DD-YYYY',
    'DD-MM-YYYY',
    'MM DD YYYY',
    'DD MM YYYY'
  ], true);

  return parsed.isValid() ? parsed.format(dateFormat) : '';
}

export function customformatDateToString(value: any): string {
  if (!value) {
    return '';
  }

  let date: Date | null = null;

  // 1️⃣ Already a Date → DO NOT reparse
  if (value instanceof Date) {
    date = value;
  }

  // 2️⃣ ISO string (UTC or local)
  else if (typeof value === 'string' && value.includes('T')) {
    date = new Date(value);
  }

  // 3️⃣ Formatted string: MM-DD-YYYY hh:mm A
  else if (typeof value === 'string' && value.includes('-')) {
    const parts = value.split(/[- :]/);

    if (parts.length < 5) {
      return '';
    }

    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    let hour = parseInt(parts[3], 10);
    const minute = parseInt(parts[4], 10);
    const ampm = parts[5]?.toUpperCase();

    // 12-hour → 24-hour conversion
    if (ampm === 'PM' && hour !== 12) {
      hour += 12;
    } else if (ampm === 'AM' && hour === 12) {
      hour = 0;
    }

    date = new Date(year, month - 1, day, hour, minute);
  }

  if (!date || isNaN(date.getTime())) {
    return '';
  }

  // 4️⃣ Manual formatting (NO timezone conversion)
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12;

  return `${mm}-${dd}-${yyyy} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
}

/**
 * Parses Equifax US date strings and returns a UI-formatted date.
 * - 8 digits: MMDDYYYY → MM-DD-YYYY
 * - 6 digits: MMYYYY (e.g. 012026 = Jan 2026) → MM/YYYY
 *
 * @param value Raw date string from Equifax US API
 * @param separator Date part separator: '-' for mm-dd-yyyy (default), '/' for mm/dd/yyyy
 * @returns Formatted string for display, or empty string if invalid/missing
 */
export function formatEquifaxUSDateToUI(
  value: string | null | undefined,
  separator: '-' | '/' = '-'
): string {
  if (value == null || typeof value !== 'string') {
    return '';
  }
  const s = value.trim();
  if (s.length === 8 && /^\d{8}$/.test(s)) {
    const mm = s.slice(0, 2);
    const dd = s.slice(2, 4);
    const yyyy = s.slice(4, 8);
    const day = parseInt(dd, 10);
    if (day === 0) {
      return `${mm}${separator}${yyyy}`;
    }
    return `${mm}${separator}${dd}${separator}${yyyy}`;
  }
  if (s.length === 6 && /^\d{6}$/.test(s)) {
    const mm = s.slice(0, 2);
    const yyyy = s.slice(2, 6);
    return `${mm}${separator}${yyyy}`;
  }
  return s || '';
}

/**
 * Parses Equifax US date filed format (MM00YYYY, e.g. 07002022) and returns YYYY-MM.
 *
 * @param value Raw date string from Equifax US API
 * @returns Formatted string for display (e.g. "2022-07"), or empty string if invalid/missing
 */
export function formatEquifaxUSDateFiledToYYYYMM(value: string | null | undefined): string {
  if (value == null || typeof value !== 'string') {
    return '';
  }
  const s = value.trim();
  if (s.length === 8 && /^\d{8}$/.test(s)) {
    const mm = s.slice(0, 2);
    const yyyy = s.slice(4, 8);
    return `${yyyy}-${mm}`;
  }
  return s || '';
}

/**
 * Parses Equifax US date (MMDDYYYY) and returns YYYY-MM-DD.
 *
 * @param value Raw date string from Equifax US API
 * @returns Formatted string for display, or empty string if invalid/missing
 */
export function formatEquifaxUSDateToYYYYMMDD(value: string | null | undefined): string {
  if (value == null || typeof value !== 'string') {
    return '';
  }
  const s = value.trim();
  if (s.length === 8 && /^\d{8}$/.test(s)) {
    const mm = s.slice(0, 2);
    const dd = s.slice(2, 4);
    const yyyy = s.slice(4, 8);
    return `${yyyy}-${mm}-${dd}`;
  }
  return s || '';
}

